import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, User as UserIcon, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { EngineerAvatar } from "@/components/installer/EngineerAvatar";

/**
 * Assignment strip that lives inside the installer project detail header.
 *
 * FIELD-001B · The user-facing label is "Project Lead". API field names
 * (lead_engineer_id/name/colour) are preserved for compatibility. The
 * Change/Clear affordances are gated on `manage_projects` — ordinary
 * Engineers see a read-only chip only.
 */
export function LeadEngineerRow({ project, onAssigned }) {
    const { user } = useAuth();
    const canManage = Array.isArray(user?.permissions)
        && user.permissions.includes("manage_projects");
    const [open, setOpen] = useState(false);
    const [engineers, setEngineers] = useState([]);
    const [query, setQuery] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.listEngineers().then(setEngineers).catch(() => setEngineers([]));
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? engineers.filter((e) => (e.displayName || "").toLowerCase().includes(q)) : engineers;
    }, [engineers, query]);

    const assign = async (id) => {
        setSaving(true);
        try {
            const updated = await api.assignLeadEngineer(project.id, id);
            onAssigned?.(updated);
            toast.success(id ? "Project Lead assigned" : "Project Lead cleared");
            setOpen(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Assignment failed");
        } finally { setSaving(false); }
    };

    const currentId = project.lead_engineer_id;
    const chip = currentId ? {
        id: currentId,
        displayName: project.lead_engineer_name,
        colour: project.lead_engineer_colour,
        initials: project.lead_engineer_initials,
        role: project.lead_engineer_role,
        firstName: (project.lead_engineer_name || "").split(" ")[0],
        lastName: (project.lead_engineer_name || "").split(" ").slice(1).join(" "),
    } : null;

    return (
        <div className="mt-4 rounded-[16px] bg-white/8 border border-white/10 backdrop-blur px-4 py-3 flex items-center gap-3" data-testid="lead-engineer-row">
            <div className="text-white/60 text-[11px] tracking-[0.12em] font-medium uppercase shrink-0">Project Lead</div>
            <div className="flex-1 min-w-0">
                {chip ? (
                    <div className="flex items-center gap-2.5">
                        <EngineerAvatar eng={chip} size={30} />
                        <div className="min-w-0">
                            <div className="text-sm text-white font-medium truncate" data-testid="lead-engineer-name">{chip.displayName}</div>
                            <div className="text-[11px] text-white/60 truncate">{chip.role}</div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 text-white/70 text-sm">
                        <UserIcon size={14} strokeWidth={1.75} /> Not assigned yet
                    </div>
                )}
            </div>
            {canManage && (
                <button
                    data-testid="lead-engineer-change"
                    onClick={() => setOpen(true)}
                    className="pe-btn-outline h-9 !border-white/25 !text-white !bg-white/5 hover:!bg-white/10"
                >
                    {chip ? "Change" : "Assign"} <ChevronDown size={13} strokeWidth={1.75} />
                </button>
            )}

            {open && canManage && (
                <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4" data-testid="lead-engineer-picker">
                    <div className="absolute inset-0 bg-black/55" onClick={() => setOpen(false)} />
                    <div className="relative bg-white rounded-[16px] w-full max-w-md max-h-[80vh] overflow-hidden shadow-2xl flex flex-col mt-16 md:mt-0">
                        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                            <div>
                                <div className="text-lg text-[#131B2F] font-medium">Assign Project Lead</div>
                                <div className="text-xs text-[#4B5565] mt-0.5">Only active engineers appear here.</div>
                            </div>
                            <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-[#F5F5F7] text-[#4B5565] flex items-center justify-center">
                                <X size={13} strokeWidth={1.75} />
                            </button>
                        </div>
                        <div className="px-4 py-3">
                            <input
                                placeholder="Search engineers…" value={query} onChange={(e) => setQuery(e.target.value)}
                                data-testid="lead-engineer-search"
                                className="w-full h-10 rounded-md border border-[#E5E7EB] px-3 text-base outline-none focus:border-[#3E9E2E]"
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto px-2 pb-2" data-testid="lead-engineer-list">
                            {chip && (
                                <button
                                    disabled={saving}
                                    onClick={() => assign(null)}
                                    data-testid="lead-engineer-clear"
                                    className="w-full text-left px-3 py-2.5 mb-1 rounded-md text-sm text-[#DA3535] hover:bg-[#FEECEC] transition-colors"
                                >
                                    Clear assignment
                                </button>
                            )}
                            {filtered.length === 0 && (
                                <div className="px-3 py-6 text-center text-sm text-[#4B5565]">No engineers match.</div>
                            )}
                            {filtered.map((e) => (
                                <button
                                    key={e.id}
                                    disabled={saving}
                                    onClick={() => assign(e.id)}
                                    data-testid={`lead-engineer-opt-${e.id}`}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[#F5F5F7] transition-colors text-left disabled:opacity-50"
                                >
                                    <EngineerAvatar eng={e} size={32} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-[#131B2F] font-medium truncate">{e.displayName}</div>
                                        <div className="text-[11px] text-[#4B5565] truncate">{e.role} · {e.email}</div>
                                    </div>
                                    {currentId === e.id && <Check size={16} className="text-[#3E9E2E]" />}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
